import { useEffect, useMemo, useRef } from 'preact/hooks'
import { SkeletonLine } from 'snippets/skeleton/skeleton'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import Image from 'snippets/image/image'
import Price from 'snippets/price/price'
import { getPrice } from 'libs/searchspring/searchspring-utils'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'

const selectors = {
  MAX_NUMBER: 4
}

function SearchDropdown({ state, closeSearch, searchUrl }) {
  const isLoading = useMemo(() => state.isLoading.value, [state.isLoading.value])
  const isOpenDropdown = useMemo(() => state.isOpenDropdown.value, [state.isOpenDropdown.value])
  const isTrendingActive = useMemo(() => state.isTrendingActive.value, [state.isTrendingActive.value])
  const searchTrendingValue = useMemo(() => state.searchTrendingValue.value, [state.searchTrendingValue.value])
  const suggestions = useMemo(() => state.suggestions.value, [state.suggestions.value])
  const results = useMemo(() => state.results.value, [state.results.value])
  const totalResults = useMemo(() => state.totalResults.value, [state.totalResults.value])
  const originalQuery = useMemo(() => state.originalQuery.value, [state.originalQuery.value])
  const { labelPopularSearches, labelSuggestedProducts, listPopularSearches } =
    GM_STATE.integrations.searchSpringConfig
  const searchDropdownRef = useRef()
  const { isDesktop } = useDetectBreakpoint()

  const handleClick = (value) => {
    state.setOriginalQuery(value)
    state.setSearchValue(value)
    state.setCorrectedQuery(value)
    state.getSuggestion(value)
  }

  useEffect(() => {
    if (isOpenDropdown) disableBodyScroll(searchDropdownRef.current)
    else clearAllBodyScrollLocks()
  }, [isOpenDropdown])

  const getCarouselClass = (result) => {
    let className = ''

    if (result.length > 3) {
      className = 'h-80'
    }

    if (result.length > 6) {
      className += ' mb-5'
    }

    return className
  }

  return (
    <>
      <div
        ref={searchDropdownRef}
        className="search-dropdown left-4 z-[100] flex max-h-[calc(100dvh-var(--header-sticky-offset-top)-136px)] flex-col overflow-y-auto rounded border border-grey-200 bg-white shadow-base lg-max:right-4 lg:w-full lg:left-1/2 lg:-translate-x-1/2 lg:max-w-[904px] lg:flex-row js-search-dropdown"
      >
        {isTrendingActive ? (
          isLoading ? (
            <div className="w-full px-4 py-5 lg:w-40 lg:py-6">
              <div className="mb-4 w-40 mt-1 lg:mb-8">{SkeletonLine(1, 'h-[15px] rounded-lg')}</div>
              {SkeletonLine(selectors.MAX_NUMBER, 'h-[15px] w-40 rounded-lg', 'gap-6 lg:gap-5')}
            </div>
          ) : (
            <div
              className={`px-4 py-5 flex w-full lg:flex-shrink-0 lg:py-6 ${results?.length > 0 ? 'lg:w-[241px] lg:border-r' : ''} ${isTrendingActive ? '!border-0' : ''}`}
            >
              {searchTrendingValue?.length > 0 ? (
                <div className="flex flex-col gap-4 lg:gap-3">
                  <h3 className="text-left text-sm font-medium text-grey-900 lg:mb-3">{labelPopularSearches}</h3>
                  {searchTrendingValue.map((item) => (
                    <h3 className="link w-fit text-sm font-light text-grey-700 no-underline hover:underline" onClick={() => handleClick(item.searchQuery)}>
                      {item.searchQuery}
                    </h3>
                  ))}
                </div>
              ) : listPopularSearches.length > 0 ? (
                <div className="flex flex-col gap-4 lg:gap-3">
                  <h3 className="text-left text-sm font-medium text-grey-900 lg:mb-3">{labelPopularSearches}</h3>
                  {listPopularSearches.map((item) => (
                    <h3 className="link w-fit text-sm font-light text-grey-700 no-underline hover:underline" onClick={() => handleClick(item)}>
                      {item}
                    </h3>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-5 text-center font-sans text-sm font-light lg:py-6">No popular search found</div>
              )}
            </div>
          )
        ) : (
          isLoading ? (
            <div className="flex w-full flex-col-reverse lg:flex-row">
              <div className="w-full px-4 pb-5 lg:w-[241px] lg:flex-shrink-0 lg:border-r lg:py-6">
                <div className="mb-4 w-40 mt-1 lg:mb-8">{SkeletonLine(1, 'h-[15px] rounded-lg')}</div>
                {SkeletonLine(selectors.MAX_NUMBER, 'h-[15px] rounded-lg w-40', 'gap-6 lg:gap-5')}
              </div>
              <div className="flex-1 px-4 py-6">
                <div className="mb-4 w-[241px] mt-1 lg-max:hidden lg:mb-8">{SkeletonLine(1, 'h-[15px] rounded-lg')}</div>
                <div className="grid grid-cols-1 gap-4 lg:hidden">
                  {[...Array(4)].map(() => (
                    <SkeletonProduct />
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 w-full lg-max:hidden">
                  {[...Array(6)].map(() => (
                    <SkeletonProduct />
                  ))}
                </div>
                {SkeletonLine(1, 'h-[15px] mt-5 w-40 rounded-lg')}
              </div>
            </div>
          ) : suggestions?.length > 0 || results?.length > 0 ? (
            <div className="flex w-full flex-col-reverse lg:flex-row lg-max:pb-5">
              {suggestions?.length > 0 ? (
                <div className={`px-4 lg:w-[241px] lg:flex-shrink-0 lg:py-6 ${results?.length > 0 ? 'lg:border-r' : ''}`}>
                  <div className="text-left">
                    <h3 className="mb-4 text-sm font-medium text-grey-900 lg:mb-6">{labelPopularSearches}</h3>
                    <div className="flex flex-col gap-3">
                      {suggestions.slice(0, 4).map((item) => (
                        <h3 className="link w-fit text-sm font-light text-grey-700 no-underline hover:underline" onClick={() => handleClick(item.text)}>
                          {item.text}
                        </h3>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="px-4 lg:w-[241px] lg:flex-shrink-0 lg:border-r lg:py-6">
                  <div className="text-left">
                    <h3 className="mb-4 text-sm font-medium text-grey-900 lg:mb-6">No popular search found</h3>
                  </div>
                </div>
              )}
              {results?.length > 0 && (
                <div className="w-full px-4 py-5 lg:w-[calc(100%-241px)] lg:py-6">
                  {isDesktop ? (
                    <>
                      <h3 className="mb-5 text-left text-sm font-medium text-grey-900">{labelSuggestedProducts}</h3>
                      <div className="w-full pb-5">
                        <CarouselSwiper
                          classWrapper="carousel-swiper w-full carousel-swiper-search-bar"
                          selector="js-carousel-swiper-search"
                          className={`lg:!px-5 ${getCarouselClass(results)}`}
                          options={{
                            slidesPerView: 2,
                            spaceBetween: 16
                          }}
                          navigation={true}
                          pagination={true}
                          scrollbar={false}
                          customPagination={true}
                          grid={{
                            rows: 3
                          }}
                        >
                          {results.map((result) => (
                            <swiper-slide className="shrink-0">
                              <SearchDropdownItom product={result} />
                            </swiper-slide>
                          ))}
                        </CarouselSwiper>
                      </div>
                    </>
                  ) : (
                    <div className="grid w-full grid-cols-1 gap-4 pb-5">
                      {results.slice(0, selectors.MAX_NUMBER).map((result) => (
                        <SearchDropdownItom product={result} />
                      ))}
                    </div>
                  )}
                  <a href={searchUrl} className="link block text-center font-light lg:text-left">
                    View all product results ({totalResults}){' '}
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-5 font-sans text-sm w-full text-center font-light lg:py-6">No results found for "{originalQuery}". Please try another search.</div>
          )
        )}
      </div>
      <div
        className="bg-search left-0 top-[calc(var(--header-sticky-offset-top)+126px)] h-screen w-full bg-dark-overlay lg:top-[calc(var(--header-sticky-offset-top)+133px)]"
        onClick={closeSearch}
      ></div>
    </>
  )
}

function SearchDropdownItom({ product }) {
  const classSizePrice = (currentProductData) => {
    let classSize = 'text-sm font-light text-grey-900'

    if (currentProductData.msrp && currentProductData.msrp !== 0) {
      const newClassSize = +currentProductData.msrp > +currentProductData.price ? ' !text-grey-300' : ''

      classSize += newClassSize
    }

    return classSize
  }

  const formatTitle = (text) => {
    return text.replace(/&amp;/g, '&')
  }

  return (
    <div className="flex w-full px-3 group lg:px-0">
      <a href={`/products/${product.handle}`} className="relative aspect-1 h-fit w-20">
        <Image
          imageUrl={[product.imageUrl]}
          alt={product.name}
          className="!fit"
          imageClass="w-full h-full fit object-cover"
          imageBreakpoints="(min-width: 768px)|(min-width: 0px)"
          isCover={true}
          isLazy={true}
          key={product.handle}
        ></Image>
      </a>
      <div className="flex-1 px-4 text-left">
        <div className="text-sm font-light text-grey-500">{product.brand}</div>
        <a href={`/products/${product.handle}`} className="mb-2 line-clamp-2 text-sm font-light text-grey-900 underline-offset-4 group-hover:underline">
          {formatTitle(product.name)}
        </a>
        <Price
          originalPrice={getPrice(product.msrp)}
          price={getPrice(product.price)}
          priceWrapperClass="flex flex-row-reverse justify-end gap-1"
          classSize={classSizePrice(product)}
        />
      </div>
    </div>
  )
}

function SkeletonProduct() {
  return (
    <div className="flex w-full px-2 h-24 lg:px-6">
      <div className="relative h-20 w-20 bg-gray-200"></div>
      <div className="flex-1 px-4 text-left flex flex-col gap-2">
        {SkeletonLine(1, 'h-[15px] rounded-lg')}
        {SkeletonLine(1, 'h-[15px] mb-2 rounded-lg')}
        {SkeletonLine(1, 'h-[15px] rounded-lg')}
      </div>
    </div>
  )
}

export default SearchDropdown
